import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Briefcase, Map, Receipt, Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './PerDiemCalculatorPage/components/CalculationForm';
import ResultsOverview from './PerDiemCalculatorPage/components/ResultsOverview';
import ExpenseBreakdown from './PerDiemCalculatorPage/components/ExpenseBreakdown';
import QuickAdjustmentSidebar from './PerDiemCalculatorPage/components/QuickAdjustmentSidebar';
import HistorySidebar from './PerDiemCalculatorPage/components/HistorySidebar';
import TipsSidebar from './PerDiemCalculatorPage/components/TipsSidebar';
import InfoContent from './PerDiemCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  destination: 'domestic', // 'domestic' или 'international'
  region: 'moscow', // регион или город для внутренних поездок
  country: 'none', // страна для международных поездок
  employeeCategory: 'regular', // 'regular', 'management', 'executive'
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date(new Date().setDate(new Date().getDate() + 3)).toISOString().split('T')[0],
  businessPurpose: 'business_meeting', // цель поездки
  transportType: 'plane', // 'plane', 'train', 'car', 'other'
  accommodationType: 'hotel', // 'hotel', 'apartment', 'hostel', 'other', 'none'
  rateOverride: false, // использовать свои ставки вместо нормативных
  customDailyRate: '700', // собственная ставка суточных (если rateOverride = true)
  includeTransportation: true, // включить расходы на транспорт
  transportationCost: '10000', // стоимость транспорта
  includeAccommodation: true, // включить расходы на проживание
  accommodationCostPerDay: '5000', // стоимость проживания за сутки
  includeAdditionalExpenses: false, // дополнительные расходы
  additionalExpensesDescription: '', // описание дополнительных расходов
  additionalExpensesAmount: '0', // сумма дополнительных расходов
  includeDocuments: true, // наличие подтверждающих документов
  documentTypes: ['tickets', 'hotel'], // типы подтверждающих документов
  advancePayment: '0', // сумма выданного аванса
  taxDeductible: true, // учитывать налоговый вычет
};

// Определение российских регионов с разными ставками суточных
export const russianRegions = [
  { id: 'moscow', name: 'Москва', dailyRate: 700, accommodationLimit: 6000 },
  { id: 'petersburg', name: 'Санкт-Петербург', dailyRate: 700, accommodationLimit: 5500 },
  { id: 'sevastopol', name: 'Севастополь', dailyRate: 700, accommodationLimit: 4500 },
  { id: 'krasnodar', name: 'Краснодарский край', dailyRate: 550, accommodationLimit: 3000 },
  { id: 'crimea', name: 'Республика Крым', dailyRate: 550, accommodationLimit: 3500 },
  { id: 'vladivostok', name: 'Владивосток', dailyRate: 600, accommodationLimit: 4000 },
  { id: 'kaliningrad', name: 'Калининград', dailyRate: 550, accommodationLimit: 3500 },
  { id: 'sochi', name: 'Сочи', dailyRate: 600, accommodationLimit: 4500 },
  { id: 'capitals', name: 'Административные центры субъектов РФ', dailyRate: 500, accommodationLimit: 3000 },
  { id: 'other', name: 'Иные населенные пункты', dailyRate: 300, accommodationLimit: 2000 },
];

// Определение популярных стран для заграничных командировок
export const foreignCountries = [
  { id: 'germany', name: 'Германия', dailyRate: 2500, accommodationLimit: 12000 },
  { id: 'france', name: 'Франция', dailyRate: 2600, accommodationLimit: 13000 },
  { id: 'uk', name: 'Великобритания', dailyRate: 2800, accommodationLimit: 16000 },
  { id: 'usa', name: 'США', dailyRate: 2800, accommodationLimit: 15000 },
  { id: 'china', name: 'Китай', dailyRate: 2400, accommodationLimit: 11000 },
  { id: 'japan', name: 'Япония', dailyRate: 2600, accommodationLimit: 14000 },
  { id: 'italy', name: 'Италия', dailyRate: 2400, accommodationLimit: 12000 },
  { id: 'spain', name: 'Испания', dailyRate: 2300, accommodationLimit: 11000 },
  { id: 'belarus', name: 'Беларусь', dailyRate: 1800, accommodationLimit: 7000 },
  { id: 'kazakhstan', name: 'Казахстан', dailyRate: 1800, accommodationLimit: 6000 },
  { id: 'turkey', name: 'Турция', dailyRate: 2200, accommodationLimit: 9000 },
  { id: 'other', name: 'Другие страны', dailyRate: 2000, accommodationLimit: 10000 },
];

// Определение категорий сотрудников
export const employeeCategories = [
  { id: 'regular', name: 'Рядовой сотрудник', rateMultiplier: 1.0 },
  { id: 'management', name: 'Руководитель среднего звена', rateMultiplier: 1.2 },
  { id: 'executive', name: 'Руководитель высшего звена', rateMultiplier: 1.5 },
];

// Определение целей командировки
export const businessPurposes = [
  { id: 'business_meeting', name: 'Деловая встреча' },
  { id: 'conference', name: 'Конференция/выставка' },
  { id: 'training', name: 'Обучение/повышение квалификации' },
  { id: 'audit', name: 'Аудит/проверка' },
  { id: 'negotiation', name: 'Переговоры с партнерами' },
  { id: 'presentation', name: 'Презентация продукта/услуги' },
  { id: 'project_management', name: 'Управление проектом на месте' },
  { id: 'other', name: 'Другое' },
];

// Определение типов документов
export const documentTypes = [
  { id: 'tickets', name: 'Билеты (авиа, ж/д и т.д.)' },
  { id: 'hotel', name: 'Счета за проживание' },
  { id: 'taxi', name: 'Чеки такси/трансфера' },
  { id: 'meals', name: 'Чеки за питание' },
  { id: 'other', name: 'Прочие документы' },
];

/**
 * PerDiemCalculatorPage Component - Полный калькулятор суточных для командировок в России и за рубежом.
 */
const PerDiemCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('per-diem-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор суточных (командировочных) | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор суточных расходов для командировок: расчет суточных, проживания, проезда и дополнительных расходов для поездок по России и за рубеж с учетом всех норм законодательства РФ.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [expenseBreakdown, setExpenseBreakdown] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalculationsData = localStorage.getItem('savedPerDiemCalculations');
    if (savedCalculationsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalculationsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'domestic':
          newState.destination = 'domestic';
          break;
        case 'international':
          newState.destination = 'international';
          newState.country = 'germany'; // пример страны по умолчанию
          break;
        case 'moscow':
          newState.destination = 'domestic';
          newState.region = 'moscow';
          break;
        case 'petersburg':
          newState.destination = 'domestic';
          newState.region = 'petersburg';
          break;
        case 'executives':
          newState.employeeCategory = 'executive';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setExpenseBreakdown([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `${formData.destination === 'domestic' ? 'Внутренняя' : 'Зарубежная'} командировка (${new Date().toLocaleDateString('ru-RU')})`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedPerDiemCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedPerDiemCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calculationToLoad = savedCalculations.find(calc => calc.id === id);
    if (calculationToLoad) {
      setFormData(calculationToLoad.formData);
      setResults(calculationToLoad.results);
      // В реальном сценарии здесь нужно будет пересчитать детализацию расходов
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Briefcase className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор суточных (командировочных)'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              expenseBreakdown={expenseBreakdown}
              setExpenseBreakdown={setExpenseBreakdown}
              russianRegions={russianRegions}
              foreignCountries={foreignCountries}
              employeeCategories={employeeCategories}
              businessPurposes={businessPurposes}
              documentTypes={documentTypes}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <ExpenseBreakdown 
                  expenses={expenseBreakdown} 
                  results={results}
                />
              </>
            )}

            {/* --- Информационный контент о командировках --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              russianRegions={russianRegions}
              foreignCountries={foreignCountries}
            />

            {/* --- Панель истории расчетов --- */}
            <HistorySidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Expense Breakdown Table */
        .expense-breakdown-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .expense-breakdown-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .expense-breakdown-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .expense-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .expense-breakdown-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .expense-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .expense-breakdown-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .expense-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .expense-breakdown-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default PerDiemCalculatorPage;